import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Menu, X, ChevronDown, LayoutDashboard, LogOut, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import logoUrl from "@/assets/fightfund-logo.png";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { to: "/how-it-works", label: "How It Works" },
  { to: "/browse", label: "Browse" },
  { to: "/pro", label: "Pro" },
  { to: "/trust-safety", label: "Trust & Safety" },
  { to: "/for-nonprofits", label: "For Nonprofits" },
  { to: "/about", label: "About" },
];

export function Logo({ light = false, to = "/" }) {
  return (
    <Link to={to} data-testid="site-logo" className="flex items-center gap-2.5">
      <span className={`flex h-9 w-9 items-center justify-center overflow-hidden rounded-full ${light ? "bg-white p-0.5" : ""}`}>
        <img src={logoUrl} alt="FightFund logo" className="h-full w-full object-contain" />
      </span>
      <span className={`font-heading text-xl font-extrabold tracking-tight ${light ? "text-white" : "text-ff-blue"}`}>
        Fight<span className="text-ff-red">Fund</span>
      </span>
    </Link>
  );
}

export default function SiteHeader() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const dashHref = (user?.role === "admin" || user?.role === "superadmin") ? "/admin/reviews" : "/organizer/dashboard";

  return (
    <header className="sticky top-0 z-50 border-b border-ff-border bg-white/90 backdrop-blur-xl">
      <div className="mx-auto grid h-16 w-full max-w-7xl grid-cols-[auto_1fr_auto] items-center gap-4 px-5 sm:px-8">
        <Logo />
        <nav className="hidden items-center justify-center gap-6 lg:flex xl:gap-8">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) =>
                `whitespace-nowrap text-sm font-semibold transition-colors duration-200 hover:text-ff-red ${isActive ? "text-ff-red" : "text-ff-dark"}`}>
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="hidden items-center justify-end gap-3 lg:flex">
          {user ? (
            <>
              <Button asChild variant="ghost" className="text-ff-dark hover:text-ff-red">
                <Link to={dashHref} data-testid="header-dashboard-link">
                  <LayoutDashboard className="mr-1.5 h-4 w-4" /> Dashboard
                </Link>
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="header-user-menu" className="flex items-center gap-2 rounded-full border border-ff-border px-3 py-1.5 text-sm font-semibold text-ff-blue hover:bg-[#F3F4F6]">
                    <User className="h-4 w-4" /> {user.name?.split(" ")[0] || "Account"}
                    <ChevronDown className="h-3.5 w-3.5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  <DropdownMenuItem asChild><Link to={dashHref} data-testid="menu-dashboard">Dashboard</Link></DropdownMenuItem>
                  <DropdownMenuItem asChild><Link to="/organizer/campaigns" data-testid="menu-campaigns">My Campaigns</Link></DropdownMenuItem>
                  <DropdownMenuItem asChild><Link to="/account/donations" data-testid="menu-donations">My Donations</Link></DropdownMenuItem>
                  <DropdownMenuItem asChild><Link to="/nonprofit/dashboard" data-testid="menu-nonprofit">Nonprofit</Link></DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="menu-logout" onClick={() => { logout(); navigate("/"); }}>
                    <LogOut className="mr-2 h-4 w-4" /> Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <Button asChild variant="ghost" className="text-ff-dark hover:text-ff-red">
              <Link to="/sign-in" data-testid="header-signin-link">Sign In</Link>
            </Button>
          )}
          <Button asChild className="bg-ff-action font-semibold text-white shadow-md hover:bg-ff-actionDark">
            <Link to="/start-a-fundraiser" data-testid="header-start-fundraiser">Start a Fundraiser</Link>
          </Button>
        </div>

        <button data-testid="mobile-menu-toggle" className="justify-self-end lg:hidden" onClick={() => setOpen((o) => !o)} aria-label="Menu">
          {open ? <X className="h-6 w-6 text-ff-blue" /> : <Menu className="h-6 w-6 text-ff-blue" />}
        </button>
      </div>

      {open && (
        <div className="border-t border-ff-border bg-white lg:hidden" data-testid="mobile-menu">
          <div className="flex flex-col gap-1 px-5 py-4">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} onClick={() => setOpen(false)}
                className="rounded-md px-3 py-2 text-sm font-semibold text-ff-dark hover:bg-[#F3F4F6]">
                {n.label}
              </Link>
            ))}
            <div className="mt-3 flex flex-col gap-2 border-t border-ff-border pt-3">
              {user ? (
                <>
                  <Button asChild variant="outline"><Link to={dashHref} onClick={() => setOpen(false)}>Dashboard</Link></Button>
                  <Button variant="ghost" onClick={() => { logout(); setOpen(false); navigate("/"); }}>Sign out</Button>
                </>
              ) : (
                <Button asChild variant="outline"><Link to="/sign-in" onClick={() => setOpen(false)}>Sign In</Link></Button>
              )}
              <Button asChild className="bg-ff-action text-white hover:bg-ff-actionDark">
                <Link to="/start-a-fundraiser" onClick={() => setOpen(false)}>Start a Fundraiser</Link>
              </Button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
